import styled from 'styled-components';
import { Wrapper as SectionWrapper, themeOptions } from '../Level3Section';
import { useEffect, useState } from 'react';
import { SyncManager } from 'db';
import { useField } from 'react-final-form';
import { useFormVehicleId } from '../hooks/useFormVehicleId';
import { useTextareaEffects } from 'hooks/useTextareaEffects';
import { useFinalReviewDialogOpened } from 'services/listerUploadVehicle/v2';

type Props = {
  theme: keyof typeof themeOptions;
};

const Header = styled.div`
  font-family: Roboto;
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  padding: 6px 8px 8px;
`;

const InputWrapper = styled.div<{ hasError: boolean }>`
  outline: ${props => (props.hasError ? '1px solid #AF2930' : '1px solid #999999')};
  border-radius: 8px;
  background: #fff;
  padding: 12px 8px;
  display: flex;
  align-items: center;
  margin: 8px;
`;

const Input = styled.textarea`
  border: none;
  font-size: 12px;
  line-height: 16px;
  width: 80%;
  font-weight: 500;
  color: #000;
  padding: 0;
  font-family: Roboto;
`;

const IconWrapper = styled.div<{ withAutoMargin?: boolean }>`
  display: flex;
  margin-left: ${props => (props.withAutoMargin ? 'auto' : 0)};
`;

const StyledError = styled.span`
  font-size: 10px;
  color: #e93323;
  padding: 0 8px;
`;

export const InspectorComments = ({ theme }: Props) => {
  const { background } = themeOptions[theme];
  const id = useFormVehicleId();

  const finalReviewOpen = useFinalReviewDialogOpened();

  const {
    input: { value: notes, onChange: changeValue }
  } = useField(`inspector_notes.message`, { subscription: { value: true } });

  const saveComment = async (comment: string) => {
    changeValue(comment);
    const syncManager = SyncManager.getInstance(id);
    await syncManager.queueVehicleUpdate(['inspector_notes.notes', comment]);
  };

  if (finalReviewOpen) return null

  return (
    <SectionWrapper id={`inspector-comments-${id}`} background={background} withBorder={theme === 'white'}>
      <Header>Inspector Comments</Header>
      <InspectorCommentsInput saveComment={saveComment} initialValue={notes} />
    </SectionWrapper>
  );
};

type InputProps = {
  initialValue: string;
  saveComment: (notes: string) => void;
};

export const InspectorCommentsInput: React.FC<InputProps> = ({ initialValue, saveComment }) => {
  const {
    input,
    meta: { touched }
  } = useField(`inspector_notes.message`, { subscription: { touched: true } });

  const [comment, setComment] = useState(initialValue || '');
  const [error, setError] = useState<string | null>(null);

  const textAreaRef = useTextareaEffects(comment);

  useEffect(() => {
    if (!comment) {
      setError('Required');
    } else if (comment.length > 500) {
      setError('Max length is 500 characters');
    } else {
      setError(null);
    }
  }, [comment]);

  useEffect(() => {
    if (comment === initialValue) return;

    const timeout = setTimeout(() => {
      saveComment(comment);
    }, 500);

    return () => clearTimeout(timeout);
  }, [comment, initialValue]);

  const hasError = !!error && touched;

  return (
    <>
      <InputWrapper hasError={hasError}>
        <Input
          ref={textAreaRef}
          placeholder="Tap here to edit"
          value={comment}
          rows={1}
          onChange={e => setComment(e.target.value)}
          onBlur={input.onBlur}
        />
      </InputWrapper>
      {hasError && <StyledError>{error}</StyledError>}
    </>
  );
};
